import { DigitalFormFieldTypeEnum, DigitalFormFieldWidthEnum, defaultFieldWidth } from 'src/app/Enums/DigitalFormEnum';
import { IDigitalFormField } from 'src/app/interfaces/digital-form';

/**
 * Layout do formulário digital, usado tanto na tela do cidadão quanto na
 * pré-visualização do construtor — o administrador vê exatamente o que será exibido.
 */

/**
 * Classe de largura. O layout é flexível: os campos de uma linha se esticam para
 * preencher a largura disponível, com um teto que impede um campo curto de virar uma
 * barra larga demais. Em telas pequenas, todos ocupam a linha inteira.
 */
const WIDTH_CLASS: Record<DigitalFormFieldWidthEnum, string> = {
    [DigitalFormFieldWidthEnum.PEQUENO]: 'is-pequeno',
    [DigitalFormFieldWidthEnum.MEDIO]: 'is-medio',
    [DigitalFormFieldWidthEnum.GRANDE]: 'is-grande',
};

/**
 * Espaço vertical que o campo ocupa, em frações de linha. É o que decide a quebra em
 * etapas: três caixas de texto cansam mais a tela do que três campos de data.
 */
const WEIGHT: Record<DigitalFormFieldWidthEnum, number> = {
    [DigitalFormFieldWidthEnum.PEQUENO]: 1,
    [DigitalFormFieldWidthEnum.MEDIO]: 2,
    [DigitalFormFieldWidthEnum.GRANDE]: 3,
};

const TEXTAREA_WEIGHT = 4;

/** Peso máximo por etapa: 12 campos pequenos, 6 médios ou 3 caixas de texto. */
export const DIGITAL_FORM_PAGE_CAPACITY = 12;

export function fieldWidth(campo: IDigitalFormField): DigitalFormFieldWidthEnum {
    if (campo.AFC_Tipo === DigitalFormFieldTypeEnum.CAIXA_TEXTO) return DigitalFormFieldWidthEnum.GRANDE;

    return WIDTH_CLASS[campo.AFC_Largura] ? campo.AFC_Largura : defaultFieldWidth(campo.AFC_Tipo);
}

export function fieldWidthClass(campo: IDigitalFormField): string {
    return WIDTH_CLASS[fieldWidth(campo)];
}

export function fieldWeight(campo: IDigitalFormField): number {
    return campo.AFC_Tipo === DigitalFormFieldTypeEnum.CAIXA_TEXTO ? TEXTAREA_WEIGHT : WEIGHT[fieldWidth(campo)];
}

/**
 * Divide os campos em etapas na ordem definida pelo administrador. O total é
 * distribuído de forma equilibrada, para não sobrar uma etapa final com um campo só.
 */
export function buildDigitalFormPages(campos: IDigitalFormField[]): IDigitalFormField[][] {
    if (!campos || campos.length === 0) return [[]];

    const pesos = campos.map(fieldWeight);
    const total = pesos.reduce((soma, peso) => soma + peso, 0);
    const totalPaginas = Math.ceil(total / DIGITAL_FORM_PAGE_CAPACITY);

    if (totalPaginas <= 1) return [campos];

    const alvo = Math.ceil(total / totalPaginas);
    const paginas: IDigitalFormField[][] = [];
    let atual: IDigitalFormField[] = [];
    let peso = 0;

    campos.forEach((campo, index) => {
        const excedeu = atual.length > 0 && peso + pesos[index] > alvo;
        const podeAbrirNova = paginas.length < totalPaginas - 1;

        if (excedeu && podeAbrirNova) {
            paginas.push(atual);
            atual = [];
            peso = 0;
        }

        atual.push(campo);
        peso += pesos[index];
    });

    if (atual.length > 0) paginas.push(atual);

    return paginas;
}
